import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastType } from 'react-toastify';

import type { User } from '@feathr/blackbox';
import { ArchiveModal, ContextMenu, MenuItem, toast } from '@feathr/components';
import { StoresContext, useAccount, useUser } from '@feathr/extender/state';
import { useToggle } from '@feathr/hooks';
import type { IRachisMessage } from '@feathr/rachis';
import { isWretchError, wretch } from '@feathr/rachis';

interface IOptionsColumnProps {
  user: User;
}

function UserOptions({ user }: IOptionsColumnProps): JSX.Element {
  const account = useAccount();
  const rootStore = useContext(StoresContext);
  const [isArchiveModalOpen, toggleArchiveModalOpen] = useToggle(false);
  const { t } = useTranslation();
  const currentUser = useUser();

  async function removeUser() {
    const currentACL = account.get('acl');
    account.set({ acl: currentACL.filter((item) => item.user_id !== user.id) });
    await account.patchDirty();
    user.collection?.remove(user.id);
    toast(t('Removed user.'), { type: 'success' });
    toggleArchiveModalOpen();
  }

  async function sendResetLink(): Promise<void> {
    const response = await wretch<IRachisMessage>(`${BLACKBOX_URL}reset-password`, {
      method: 'POST',
      body: JSON.stringify({ email: user.get('email') }),
      headers: { 'Content-Type': 'application/json' },
    });
    if (isWretchError(response)) {
      toast(t('Something went wrong. Unable to send a password reset link for this user.'), {
        type: ToastType.ERROR,
      });
      // eslint-disable-next-line no-console
      console.error(response.error.message);
      return;
    }
    toast(t('Password reset link sent to {{email}}.', { email: user.get('email') }));
  }

  // TODO: Move this function to users.ts.
  async function resendInvite(): Promise<void> {
    const response = await wretch<IRachisMessage>(`${BLACKBOX_URL}invite`, {
      method: 'POST',
      body: JSON.stringify({ email: user.get('email'), force: true }),
      headers: rootStore.Users.getHeaders(),
    });
    if (isWretchError(response)) {
      toast(t('Something went wrong. Unable to send an invite link for this user.'), {
        type: ToastType.ERROR,
      });
      // eslint-disable-next-line no-console
      console.error(response.error.message);
      return;
    }
    toast(t('Invite link sent to {{email}}.', { email: user.get('email') }));
  }

  return (
    <>
      <ContextMenu>
        <StoresContext.Provider value={rootStore}>
          {user.get('confirmed') ? (
            <MenuItem disabled={user.id === currentUser.id} onClick={sendResetLink}>
              {t('Send password reset link')}
            </MenuItem>
          ) : (
            <MenuItem disabled={user.id === currentUser.id} onClick={resendInvite}>
              {t('Resend invite link')}
            </MenuItem>
          )}
          <MenuItem
            disabled={user.id === currentUser.id}
            id={`${user.id}-archive`}
            onClick={toggleArchiveModalOpen}
          >
            {t('Remove')}
          </MenuItem>
        </StoresContext.Provider>
      </ContextMenu>
      {isArchiveModalOpen && (
        <ArchiveModal
          confirmButtonText={t('Remove user')}
          message={t('Are you sure you want to remove this user from this account?')}
          model={user}
          onClose={toggleArchiveModalOpen}
          onConfirm={removeUser}
          t={t}
          title={t('Remove User')}
        />
      )}
    </>
  );
}

export default observer(UserOptions);
